export interface Grid {
  cols: number;
  rows: number;
}

/**
 * Chooses the grid whose cells are closest to a comfortable video shape
 * (between 4:3 and 16:9) in the space available (E4-S5). Tiles fill their
 * cells, so a cell that is very wide or very tall would crop faces badly.
 * Phones show one column for one or two tiles and two for more. Their rows
 * share the height of the screen while each can be at least 150 px high;
 * with more people the rows stay 150 px high and the screen scrolls (call.css).
 */
export function gridFor(count: number, width: number, height: number, phone: boolean): Grid {
  if (count <= 0) return { cols: 1, rows: 1 };
  if (phone) {
    const cols = count <= 2 ? 1 : 2;
    return { cols, rows: Math.ceil(count / cols) };
  }
  const target = 4 / 3;
  let best: Grid = { cols: 1, rows: count };
  let bestScore = Number.POSITIVE_INFINITY;
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const aspect = width / cols / (height / rows);
    // How far the cell is from the target shape, plus a cost for empty cells.
    const score = Math.abs(Math.log(aspect / target)) + (cols * rows - count) / count;
    if (score < bestScore - 1e-9) {
      bestScore = score;
      best = { cols, rows };
    }
  }
  return best;
}

/**
 * A tablet or a computer: a viewport at least 600 CSS px wide and 500 high,
 * whichever way the device is held. A 7-inch tablet held sideways leaves the
 * app about 960 x 528. A phone is narrower than 600 held upright, and lower
 * than 450 held sideways.
 */
export const LARGE_SCREEN = '(min-width: 600px) and (min-height: 500px)';

/**
 * The simulcast layer worth asking for (E6-S5): 0 is 180p, 1 is 360p, 2 is
 * 720p, given how many tiles share the screen. Five to nine tiles on a large
 * screen are big enough that 180p looks soft, so they get 360p; a phone keeps
 * the smallest copy and spares its data plan. It is only a wish: the server
 * holds a plan without HD below 720p, and the SFU sends less when bandwidth
 * runs short.
 */
export function preferredLayer(tileCount: number, isMain: boolean, dataSaver: boolean, largeScreen: boolean): number {
  if (isMain) return dataSaver ? 1 : 2;
  if (dataSaver) return 0;
  if (tileCount <= 2) return 2;
  if (tileCount <= 4) return 1;
  if (tileCount <= 9 && largeScreen) return 1;
  return 0;
}
